<!--
  ReviewManagement.vue
  후기 관리 메인 페이지
  
  주요 기능:
  - 후기 목록 조회 및 관리
  - 후기 검색 및 필터링
  - 후기 승인/거부
  - 후기 생성/수정/삭제
  - 통계 정보 표시
-->
<template>
  <div style="padding: 1vw">
    <div class="container-wrapper">
      <div class="page-title-container">
        <div class="page-title">
          <h3 class="fw-bold" style="color: #000; font-size: 1.8rem">
            후기 관리
          </h3>
          <span class="pageDesc">
            고객 후기를 조회하고 관리할 수 있습니다.
          </span>
        </div>
        <div style="display: flex; align-items: center;">
          <!-- 통계 정보 표시 -->
          <div class="stats-container">
            <div 
              class="stat-item" 
              :class="{ active: searchFilter.reviewStatus === '' }"
              @click="filterByStatus('')"
            >
              <span class="stat-label">전체 후기</span>
              <span class="stat-value">{{ formatCommaNumber(stats.totalReviews) }}</span>
            </div>
            <div 
              class="stat-item" 
              :class="{ active: searchFilter.reviewStatus === 'pending' }"
              @click="filterByStatus('pending')"
            >
              <span class="stat-label">대기</span>
              <span class="stat-value pending">{{ formatCommaNumber(stats.pendingReviews) }}</span>
            </div>
            <div 
              class="stat-item" 
              :class="{ active: searchFilter.reviewStatus === 'review_requested' }"
              @click="filterByStatus('review_requested')"
            >
              <span class="stat-label">검수요청</span>
              <span class="stat-value review_requested">{{ formatCommaNumber(stats.requestedReviews) }}</span>
            </div>
            <div 
              class="stat-item" 
              :class="{ active: searchFilter.reviewStatus === 'exposed' }"
              @click="filterByStatus('exposed')"
            >
              <span class="stat-label">노출중</span>
              <span class="stat-value exposed">{{ formatCommaNumber(stats.exposedReviews) }}</span>
            </div>
            <div 
              class="stat-item" 
              :class="{ active: searchFilter.reviewStatus === 'hidden' }"
              @click="filterByStatus('hidden')"
            >
              <span class="stat-label">비노출</span>
              <span class="stat-value hidden">{{ formatCommaNumber(stats.hiddenReviews) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 검색 필터 영역 -->
    <ReviewSearchFilter
      :search-filter="searchFilter"
      :manufacturer-list="manufacturerList"
      :category-list="categoryList"
      :dept-list="deptList"
      @search="handleSearch"
      @reset="handleReset"
    />

    <!-- 검수요청 후기 테이블 -->
    <PendingReviewTable 
      ref="pendingReviewTableRef"
      @status-changed="handlePendingStatusChanged"
      @detail="handleDetail"
    />

    <div class="container-wrapper">
      <div class="col-xs-12 table-info-filter">
        <div class="total-div">
          <p>
            전체 <span>{{ formatCommaNumber(listSize) }}</span>건
          </p>
        </div>
        <div class="search-container">
          <a-button
            @click="downloadExcel"
            type="primary"
            :loading="excelLoading"
          >
            <i class="fa-solid fa-download"/>
            엑셀 다운로드
          </a-button>
          <a-button
            @click="bulkStatusChange('exposed')"
            type="primary"
            class="yellow-button"
            v-if="selectedRowKeys.length > 0"
          >
            <i class="fa-solid fa-eye"/>
            일괄 노출 ({{ selectedRowKeys.length }})
          </a-button>
          <a-button
            @click="bulkStatusChange('hidden')"
            type="primary"
            danger
            v-if="selectedRowKeys.length > 0"
          >
            <i class="fa-solid fa-eye-slash"/>
            일괄 비노출 ({{ selectedRowKeys.length }})
          </a-button>
          <a-button
            @click="bulkDelete"
            type="primary"
            danger
            v-if="selectedRowKeys.length > 0"
          >
            <i class="fa-solid fa-trash"/>
            일괄 삭제 ({{ selectedRowKeys.length }})
          </a-button>
          <select class="search-select" v-model="rowsPerPage" @change="handleSearch">
            <option :value="10">10개씩 보기</option>
            <option :value="20">20개씩 보기</option>
            <option :value="50">50개씩 보기</option>
          </select>
        </div>
      </div>
    </div>

    <!-- 후기 목록 테이블 -->
    <div class="container-wrapper">
      <a-table 
        v-if="reviewList.length > 0"
        class="ant-table-striped"
        size="small"
        :columns="columns"
        :data-source="reviewList"
        :pagination="false"
        :row-selection="rowSelection"
        :loading="loading"
        row-key="_id"
        :resizable="false"
        table-layout="fixed"
      >
        <template #emptyText>
          <div class="noResult">검색 결과가 없습니다.</div>
        </template>
      </a-table>
      
      <div v-else-if="!loading" class="empty-state">
        <i class="fa fa-comment-o" style="font-size: 48px; color: #ccc; margin-bottom: 16px"></i>
        <p>아직 등록된 후기가 없습니다.</p>
      </div>
    </div>

    <!-- 페이지네이션 -->
    <pagination 
      :page-info="paginationData"
      page-name="ReviewList"
      @change-page="paginationHandler"
    />



    <!-- 후기 상세 모달 -->
    <ReviewDetailModal
      :open="detailModalVisible"
      :review-data="currentReviewData"
      @close="handleDetailModalClose"

      @delete="handleDelete"
      @approve="handleApprove"
    />
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, nextTick, getCurrentInstance, shallowRef, markRaw, onBeforeUnmount } from 'vue';
import { message } from 'ant-design-vue';
import { twoBtnDialog, formatCommaNumber, convertDate, convertToLocalTime, phoneNumberFormatter } from '@/utils/utils';
import { MedicalDept } from '@/enums/common.enums';
import Pagination from '@/views/components/Pagination.vue';
import ReviewSearchFilter from './components/ReviewSearchFilter.vue';
import PendingReviewTable from './components/PendingReviewTable.vue';
import ReviewDetailModal from './components/ReviewDetailModal.vue';

export default {
  name: 'ReviewManagement',
  components: {
    Pagination,
    ReviewSearchFilter,
    PendingReviewTable,
    ReviewDetailModal
  },
  setup() {
    // API 인스턴스 초기화
    const instance = getCurrentInstance();
    let apiInstance = null;
    
    if (instance && instance.proxy) {
      apiInstance = instance.proxy.$apiInstance;
    } else {
      console.error('Current instance not available, API initialization failed');
    }

    // 반응형 상태 정의 (shallowRef로 성능 최적화)
    const loading = ref(false);
    const excelLoading = ref(false);
    const detailModalVisible = ref(false);

    // 데이터 상태 (대용량 데이터는 shallowRef 사용)
    const reviewList = shallowRef([]);
    const companyList = shallowRef([]);
    const manufacturerList = shallowRef([]);
    const categoryList = shallowRef([]);
    const deptList = shallowRef([]);
    const currentReviewData = ref({});
    const selectedRowKeys = ref([]);
    
    // 컴포넌트 참조
    const pendingReviewTableRef = ref(null);
    
    // 통계 정보
    const stats = reactive({
      totalReviews: 0,
      exposedReviews: 0,
      requestedReviews: 0,
      pendingReviews: 0,
      hiddenReviews: 0
    });

    // 검색 필터
    const searchFilter = reactive({
      filter: '',
      keyword: '',
      reviewStatus: '',
      reviewType: '',
      selectedCompany: '',
      selectedManufacturer: '',
      selectedCategory: '',
      selectedDept: '',
      startAt: null,
      endAt: null,
      selectedDateFilter: 'all',
      dateFilters: [3, 12, 36, 'etc'],
      isTestCompany: '' // 테스트병원 필터 추가
    });
    
    // 페이지네이션
    const listSize = ref(0);
    const rowsPerPage = ref(10);
    const paginationData = reactive({
      currentPage: 1,
      totalPages: 0
    });

    // 상태 확인 헬퍼 함수들 (성능 최적화를 위해 상단으로 이동)
    const isExposed = (record) => {
      return record.reviewStatus === 'exposed';
    };

    const isHidden = (record) => {
      return record.reviewStatus === 'hidden';
    };

    // 새로운 대기 상태 확인 함수 추가
    const isPending = (record) => {
      return record.reviewStatus === 'pending';
    };

    const isRequested = (record) => {
      return record.reviewStatus === 'review_requested';
    };

    // 상태 매핑 캐시 (한 번만 생성)
    const statusMap = markRaw({
      'exposed': { text: '노출중', class: 'exposed' },
      'review_requested': { text: '검수요청', class: 'review_requested' },
      'pending': { text: '대기', class: 'pending' },
      'hidden': { text: '비노출', class: 'hidden' }
    });

    // 렌더링 함수들을 외부로 추출하여 재사용성 향상
    const renderPhoneNumber = ({ record }) => {
      return phoneNumberFormatter(record.phoneNumber);
    };

    const renderSurgeryDate = ({ record }) => {
      return convertDate(record.surgeryAt);
    };

    const renderContent = ({ record }) => {
      const content = record.content || '';
      return content.length > 100 ? content.substring(0, 100) + '...' : content;
    };

    const renderCreateTime = ({ record }) => {
      return convertToLocalTime(record.createAt);
    };

    // 키워드 렌더링
    const renderKeywords = ({ record }) => {
      const keywords = record.satisfactionPoints;
      if (!keywords || keywords.length === 0) {
        return '-';
      }
      
      // 각 키워드를 줄바꿈으로 표시 (PendingReviewTable과 동일한 스타일)
      if (Array.isArray(keywords)) {
        return (
          <div class="keywords-container">
            {keywords.map((keyword, index) => (
              <div key={index} class="keyword-item">
                {keyword}
              </div>
            ))}
          </div>
        );
      }
      return keywords;
    };

    // 상태 렌더링 최적화
    const renderStatus = ({ record }) => {
      const statusInfo = statusMap[record.reviewStatus] || statusMap['review_requested'];
      
      return (
        <div class="status-chip-wrap">
          <div class={['status-chip', statusInfo.class]}>
            {statusInfo.text}
          </div>
        </div>
      );
    };

    // 작업 버튼 렌더링 최적화
    const renderActions = ({ record }) => {
      const exposed = isExposed(record);
      const hidden = isHidden(record);
      
      return (
        <div style="display: flex; gap: 8px; justify-content: center;">
          <a-button size="small" onClick={() => handleDetail(record)}>
            상세
          </a-button>
          <a-button 
            size="small" 
            danger 
            onClick={() => handleDelete(record._id)}
          >
            삭제
          </a-button>
          {!exposed && (
            <a-button 
              size="small" 
              type="primary" 
              class="yellow-button"
              onClick={() => handleStatusChange(record._id, 'exposed')}
            >
              노출
            </a-button>
          )}
          {!hidden && (
            <a-button 
              size="small" 
              danger
              onClick={() => handleStatusChange(record._id, 'hidden')}
            >
              비노출
            </a-button>
          )}
        </div>
      );
    };
    
    // 테이블 컬럼 정의 (markRaw로 불필요한 반응성 제거)
    const columns = markRaw([
      {
        title: '번호',
        dataIndex: 'index',
        key: 'index',
        width: 60,
        align: 'center'
      },
      {
        title: '고객명',
        dataIndex: 'customerName',
        key: 'customerName',
        width: 120,
        align: 'center'
      },
      {
        title: '전화번호',
        dataIndex: 'phoneNumber',
        key: 'phoneNumber',
        width: 150,
        align: 'center',
        customRender: renderPhoneNumber
      },
      {
        title: '병원명',
        dataIndex: 'companyName',
        key: 'companyName',
        width: 150,
        align: 'center'
      },
      {
        title: '수술일자',
        dataIndex: 'surgeryAt',
        key: 'surgeryAt',
        width: 150,
        align: 'center',
        customRender: renderSurgeryDate
      },
      {
        title: '수술명',
        dataIndex: 'categoryName',
        key: 'categoryName',
        width: 120,
        align: 'center'
      },
      {
        title: '후기 내용',
        dataIndex: 'content',
        key: 'content',
        width: 300,
        customRender: renderContent
      },
      {
        title: '만족도',
        dataIndex: 'score',
        key: 'score',
        width: 80,
        align: 'center'
      },
      {
        title: '키워드',
        dataIndex: 'satisfactionPoints',
        key: 'satisfactionPoints',
        width: 200,
        align: 'center',
        customRender: renderKeywords
      },
      {
        title: '등록일시',
        dataIndex: 'createAt',
        key: 'createAt',
        width: 150,
        align: 'center',
        customRender: renderCreateTime
      },
      {
        title: '상태',
        dataIndex: 'reviewStatus',
        key: 'reviewStatus',
        width: 120,
        align: 'center',
        customRender: renderStatus
      },
      {
        title: '작업',
        key: 'action',
        width: 300,
        align: 'center',
        customRender: renderActions
      }
    ]);

    // 행 선택 설정 (computed 최적화)
    const rowSelection = computed(() => ({
      selectedRowKeys: selectedRowKeys.value,
      onChange: (selectedKeys) => {
        selectedRowKeys.value = selectedKeys;
      },
      // 성능 최적화: 선택 시 전체 테이블 재렌더링 방지
      preserveSelectedRowKeys: true
    }));

    // 防抖搜索优化
    let searchTimeout = null;
    const debouncedSearch = (callback, delay = 300) => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
      searchTimeout = setTimeout(callback, delay);
    };

    // 공통 에러 처리 함수
    const handleError = (error, defaultMessage) => {
      console.error('Error occurred:', error);
      
      let errorMessage = defaultMessage;
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      message.error(errorMessage);
      return errorMessage;
    };

    // 후기 목록 로드
    const loadReviews = async (shouldLoadStats = true) => {
      if (!apiInstance) {
        message.error('API가 초기화되지 않았습니다.');
        return;
      }
      
      // 통계 정보는 필요할 때만 로드
      if (shouldLoadStats) {
        loadStats();
      }
      
      loading.value = true;
      
      try {
        const params = {
          page: paginationData.currentPage,
          limit: rowsPerPage.value
        };
        
        // 검색 조건 추가
        if (searchFilter.keyword.trim()) {
          if (searchFilter.filter === '' || searchFilter.filter === 'all') {
            // 전체 검색 - 모든 필드에서 검색 (백엔드에서 search 파라미터 지원하는 경우)
            params.keyword = searchFilter.keyword.trim();
          } else {
            // 특정 필드 검색
            params[searchFilter.filter] = searchFilter.keyword.trim();
          }
        }

        if (searchFilter.reviewStatus) params.reviewStatus = searchFilter.reviewStatus;
        if (searchFilter.reviewType) params.reviewType = searchFilter.reviewType;
        if (searchFilter.selectedCompany) params.companyId = searchFilter.selectedCompany;
        if (searchFilter.selectedManufacturer) params.manufacturerId = searchFilter.selectedManufacturer;
        if (searchFilter.selectedCategory) params.categoryId = searchFilter.selectedCategory;
        if (searchFilter.selectedDept) params.medicalDept = searchFilter.selectedDept;
        if (searchFilter.isTestCompany) params.isTestCompany = searchFilter.isTestCompany; // 테스트병원 필터 파라미터 추가
        
        // 날짜 매개변수 추가 (HistoryManage.vue 방식 참조)
        if (searchFilter.startAt && searchFilter.endAt) {
          params.startAt = unixTimeToYYYYMMDD(searchFilter.startAt);
          params.endAt = unixTimeToYYYYMMDD(searchFilter.endAt);
        }
        
        const response = await apiInstance.get('/reviews', { params });
        
        if (response.data) {
          const data = response.data.data || [];
          listSize.value = response.data.pagination?.total || 0;
          paginationData.totalPages = response.data.pagination?.totalPages || 0;
          
          // 성능 최적화: 번호 설정을 한 번에 처리
          const processedData = data.map((item, index) => ({
            ...item,
            index: listSize.value - index - (paginationData.currentPage - 1) * rowsPerPage.value
          }));

          reviewList.value = processedData;
        }
      } catch (error) {
        handleError(error, '후기 목록을 불러오는 중 오류가 발생했습니다.');
        reviewList.value = [];
        listSize.value = 0;
      } finally {
        loading.value = false;
      }
    };

    // 날짜 변환 유틸리티 함수들 (HistoryManage.vue에서 참조)
    const unixTimeToYYYYMMDD = (unixTime) => {
      const date = new Date(unixTime);
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      return `${year}${month}${day}`;
    };

    // 통계 정보 로드
    const loadStats = async () => {
      if (!apiInstance) return;
      
      try {
        const params = {};
        
        // 테스트병원 필터가 설정되어 있으면 파라미터 추가
        if (searchFilter.isTestCompany) {
          params.isTestCompany = searchFilter.isTestCompany;
        }
        
        const response = await apiInstance.get('/reviews/status-stats', { params });
        
        if (response.data) {
          stats.totalReviews = response.data.totalReviews || response.data.statusStats?.total || 0;
          stats.exposedReviews = response.data.statusStats?.exposed || 0;
          stats.requestedReviews = response.data.statusStats?.reviewRequested || 0;
          stats.pendingReviews = response.data.statusStats?.pending || 0;
          stats.hiddenReviews = response.data.statusStats?.hidden || 0;
        }
      } catch (error) {
        console.error('통계 정보 로드 실패:', error);
      }
    };

    // 기본 데이터 로드 (병렬 처리로 최적화)
    const loadBasicData = async () => {
      if (!apiInstance) return;
      
      try {
        // 진료과와 제조사 데이터를 병렬로 로드
        const [deptResponse, manufacturerResponse] = await Promise.all([
          apiInstance.get('/items/medical/depts'),
          apiInstance.post('/companies/list/v2', {
            page: 1,
            limit: 1000,
            type: "MANUFACTURER"
          })
        ]);
        
        // 진료과 데이터 처리
        if (deptResponse.status === 200 || deptResponse.status === 201) {
          deptList.value = deptResponse.data.filter(item => item.code !== MedicalDept.COMMON) || [];
        }
        
        // 제조사 데이터 처리
        if (manufacturerResponse.status === 200 || manufacturerResponse.status === 201) {
          manufacturerList.value = manufacturerResponse.data.docs || [];
        }
        
      } catch (error) {
        console.error('기본 데이터 로드 실패:', error);
      }
    };

    // 검색 처리
    const handleSearch = () => {
      debouncedSearch(() => {
        paginationData.currentPage = 1;
        selectedRowKeys.value = [];
        loadReviews();
        
        // 검수요청 테이블도 함께 새로고침
        if (pendingReviewTableRef.value && pendingReviewTableRef.value.loadPendingReviews) {
          pendingReviewTableRef.value.loadPendingReviews();
        }
      });
    };

    // 검색 초기화
    const handleReset = () => {
      Object.assign(searchFilter, {
        filter: '',
        keyword: '',
        reviewStatus: '',
        reviewType: '',
        selectedCompany: '',
        selectedManufacturer: '',
        selectedCategory: '',
        selectedDept: '',
        startAt: null,
        endAt: null,
        selectedDateFilter: 'all',
        isTestCompany: '' // 테스트병원 필터 초기화
      });
      paginationData.currentPage = 1;
      selectedRowKeys.value = [];
      loadReviews();
      
      // 검수요청 테이블도 함께 새로고침
      if (pendingReviewTableRef.value && pendingReviewTableRef.value.loadPendingReviews) {
        pendingReviewTableRef.value.loadPendingReviews();
      }
    };

    // 페이지네이션 처리
    const paginationHandler = (pageInfo) => {
      paginationData.currentPage = pageInfo.page;
      selectedRowKeys.value = []; // 페이지 변경 시 선택 초기화
      
      // 페이지 변경 시 통계는 새로고침하지 않음
      loadReviews(false);
    };

    // 후기 상세 보기
    const handleDetail = (record) => {
      currentReviewData.value = { ...record };
      detailModalVisible.value = true;
    };

    // 후기 삭제
    const handleDelete = async (id) => {
      const isConfirmed = await twoBtnDialog(
        '후기 삭제',
        '정말로 이 후기를 삭제하시겠습니까?',
        '삭제'
      );
      
      if (!isConfirmed) return;
      
      try {
        await apiInstance.delete(`/reviews/${id}`);
        message.success('후기가 성공적으로 삭제되었습니다.');
        
        // 성능 최적화: 로컬에서 먼저 제거하고 필요시에만 API 호출
        reviewList.value = reviewList.value.filter(item => item._id !== id);
        listSize.value = Math.max(0, listSize.value - 1);
        
        // 현재 페이지에 데이터가 없으면 이전 페이지로
        if (reviewList.value.length === 0 && paginationData.currentPage > 1) {
          paginationData.currentPage--;
          loadReviews();
        }
      } catch (error) {
        handleError(error, '후기 삭제 중 오류가 발생했습니다.');
        // 삭제 실패 시 데이터 다시 로드
        loadReviews();
      }
    };

    // 후기 상태 변경
    const handleStatusChange = async (id, reviewStatus) => {
      const statusText = {
        'exposed': '노출',
        'review_requested': '검수요청',
        'pending': '대기',
        'hidden': '비노출'
      };
      
      try {
        await apiInstance.patch(`/reviews/${id}/status`, { reviewStatus });
        message.success(`후기가 성공적으로 ${statusText[reviewStatus]}로 변경되었습니다.`);
        
        // 성능 최적화: 로컬 상태 업데이트
        const index = reviewList.value.findIndex(item => item._id === id);
        if (index !== -1) {
          reviewList.value[index].reviewStatus = reviewStatus;
          // shallowRef를 사용하므로 수동으로 트리거
          reviewList.value = [...reviewList.value];
        }
        loadReviews();
        
        // 상태 변경 시에만 PendingReviewTable 새로고침
        if (pendingReviewTableRef.value && pendingReviewTableRef.value.loadPendingReviews) {
          pendingReviewTableRef.value.loadPendingReviews();
        }
      } catch (error) {
        handleError(error, `후기 상태 변경 중 오류가 발생했습니다.`);
        // 상태 변경 실패 시 데이터 다시 로드
        loadReviews();
      }
    };

    // 일괄 상태 변경
    const bulkStatusChange = async (reviewStatus) => {
      if (selectedRowKeys.value.length === 0) {
        message.warning('상태를 변경할 후기를 선택해주세요.');
        return;
      }
      
      const statusText = {
        'exposed': '노출',
        'review_requested': '검수요청',
        'pending': '대기',
        'hidden': '비노출'
      };
      
      const isConfirmed = await twoBtnDialog(
        `일괄 ${statusText[reviewStatus]}`,
        `선택한 ${selectedRowKeys.value.length}개의 후기를 ${statusText[reviewStatus]}로 변경하시겠습니까?`,
        statusText[reviewStatus]
      );
      
      if (!isConfirmed) return;
      
      try {
        const promises = selectedRowKeys.value.map(id => 
          apiInstance.patch(`/reviews/${id}/status`, { reviewStatus })
        );
        
        await Promise.all(promises);
        message.success(`${selectedRowKeys.value.length}개의 후기가 ${statusText[reviewStatus]}로 변경되었습니다.`);
        
        // 성능 최적화: 로컬 상태 일괄 업데이트
        const updatedList = reviewList.value.map(item => {
          if (selectedRowKeys.value.includes(item._id)) {
            return { ...item, reviewStatus };
          }
          return item;
        });
        reviewList.value = updatedList;
        
        selectedRowKeys.value = [];
        
        // 일괄 상태 변경 시에만 PendingReviewTable 새로고침
        if (pendingReviewTableRef.value && pendingReviewTableRef.value.loadPendingReviews) {
          pendingReviewTableRef.value.loadPendingReviews();
        }
      } catch (error) {
        handleError(error, `일괄 ${statusText[reviewStatus]} 중 오류가 발생했습니다.`);
        // 실패 시 데이터 다시 로드
        loadReviews();
      }
    };

    // 일괄 삭제
    const bulkDelete = async () => {
      if (selectedRowKeys.value.length === 0) {
        message.warning('삭제할 후기를 선택해주세요.');
        return;
      }
      
      const isConfirmed = await twoBtnDialog(
        '일괄 삭제',
        `선택한 ${selectedRowKeys.value.length}개의 후기를 삭제하시겠습니까?`,
        '삭제'
      );
      
      if (!isConfirmed) return;
      
      try {
        const promises = selectedRowKeys.value.map(id => 
          apiInstance.delete(`/reviews/${id}`)
        );
        
        await Promise.all(promises);
        message.success(`${selectedRowKeys.value.length}개의 후기가 삭제되었습니다.`);
        
        // 성능 최적화: 로컬에서 일괄 제거
        const deletedCount = selectedRowKeys.value.length;
        reviewList.value = reviewList.value.filter(item => 
          !selectedRowKeys.value.includes(item._id)
        );
        listSize.value = Math.max(0, listSize.value - deletedCount);
        
        selectedRowKeys.value = [];
        
        // 현재 페이지에 데이터가 없으면 이전 페이지로
        if (reviewList.value.length === 0 && paginationData.currentPage > 1) {
          paginationData.currentPage--;
          loadReviews();
        }
      } catch (error) {
        handleError(error, '일괄 삭제 중 오류가 발생했습니다.');
        // 실패 시 데이터 다시 로드
        loadReviews();
      }
    };

    // 일괄 승인 (기존 호환성 유지)
    const bulkApprove = async () => {
      await bulkStatusChange('exposed');
    };

    // 후기 승인/거부 (기존 호환성 유지)
    const handleApprove = async (id, isApproved) => {
      const reviewStatus = isApproved ? 'exposed' : 'hidden';
      await handleStatusChange(id, reviewStatus);
    };

    // 상세 모달 닫기
    const handleDetailModalClose = () => {
      detailModalVisible.value = false;
      currentReviewData.value = {};
    };

    // 상태별 필터링
    const filterByStatus = (status) => {
      // 전체 후기인 경우 빈 문자열로 설정
      searchFilter.reviewStatus = status;
      
      // 페이지를 1페이지로 초기화
      paginationData.currentPage = 1;
      
      // 선택된 행 초기화
      selectedRowKeys.value = [];
      
      // 데이터 로드
      loadReviews();
      
      // PendingReviewTable도 함께 새로고침
      if (pendingReviewTableRef.value && pendingReviewTableRef.value.loadPendingReviews) {
        pendingReviewTableRef.value.loadPendingReviews();
      }
    };

    // 검수요청 테이블에서 상태 변경 시 호출
    const handlePendingStatusChanged = () => {
      // 메인 테이블과 통계 정보 갱신
      loadReviews();
    };

    // 컴포넌트 언마운트 시 정리
    const cleanup = () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };

    // 컴포넌트 마운트 시 데이터 로드 (병렬 처리로 최적화)
    onMounted(async () => {
      // 기본 데이터와 후기 목록을 병렬로 로드하여 성능 향상
      await Promise.all([
        loadBasicData(),
        loadReviews()
      ]);
    });

    // 컴포넌트 언마운트 시 정리
    onBeforeUnmount(() => {
      cleanup();
    });

    // 엑셀 다운로드
    const downloadExcel = async () => {
      if (!apiInstance) {
        message.error('API가 초기화되지 않았습니다.');
        return;
      }
      
      excelLoading.value = true;
      
      try {
        const params = {};
        
        // 현재 검색 조건 추가
        if (searchFilter.keyword.trim()) {
          if (searchFilter.filter === '' || searchFilter.filter === 'all') {
            params.keyword = searchFilter.keyword.trim();
          } else {
            params[searchFilter.filter] = searchFilter.keyword.trim();
          }
        }
        
        if (searchFilter.reviewStatus) params.reviewStatus = searchFilter.reviewStatus;
        if (searchFilter.reviewType) params.reviewType = searchFilter.reviewType;
        if (searchFilter.selectedCompany) params.companyId = searchFilter.selectedCompany;
        if (searchFilter.selectedManufacturer) params.manufacturerId = searchFilter.selectedManufacturer;
        if (searchFilter.selectedCategory) params.categoryId = searchFilter.selectedCategory;
        if (searchFilter.selectedDept) params.medicalDept = searchFilter.selectedDept;
        if (searchFilter.isTestCompany) params.isTestCompany = searchFilter.isTestCompany; // 테스트병원 필터 파라미터 추가
        
        // 날짜 매개변수 추가
        if (searchFilter.startAt && searchFilter.endAt) {
          params.startAt = unixTimeToYYYYMMDD(searchFilter.startAt);
          params.endAt = unixTimeToYYYYMMDD(searchFilter.endAt);
        }
        
        const response = await apiInstance.get('/reviews/download/excel', { 
          params,
          responseType: 'blob'
        });
        
        // 파일 다운로드 처리
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        // 파일명 생성 (현재 날짜 포함)
        const now = new Date();
        const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
        link.download = `후기관리_${dateStr}.xlsx`;
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        message.success('엑셀 파일이 다운로드되었습니다.');
      } catch (error) {
        handleError(error, '엑셀 다운로드 중 오류가 발생했습니다.');
      } finally {
        excelLoading.value = false;
      }
    };

    return {
      // 상태
      loading,
      excelLoading,
      detailModalVisible,
      
      // 데이터
      reviewList,
      companyList,
      manufacturerList,
      categoryList,
      deptList,
      currentReviewData,
      selectedRowKeys,
      stats,
      
      // 컴포넌트 참조
      pendingReviewTableRef,
      
      // 검색 및 페이지네이션
      searchFilter,
      listSize,
      rowsPerPage,
      paginationData,
      
      // 테이블
      columns,
      rowSelection,
      
      // 이벤트 핸들러
      handleSearch,
      handleReset,
      paginationHandler,
      handleDetail,
      handleDelete,
      handleApprove,
      handleStatusChange,
      bulkApprove,
      bulkStatusChange,
      bulkDelete,
      handleDetailModalClose,
      downloadExcel,
      filterByStatus,
      handlePendingStatusChanged,
      
      // 유틸 함수
      formatCommaNumber,
      convertToLocalTime,
      isExposed,
      isHidden,
      isPending,  
      isRequested
    };
  }
};
</script>

<style scoped lang="scss">
@import '@/assets/scss/certificates.scss';

.container-wrapper {
  margin-bottom: 20px;
}

.page-title-container {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}

.page-title {
  h3 {
    margin-bottom: 8px;
  }
  
  .pageDesc {
    color: #6c757d;
    font-size: 14px;
  }
}

.stats-container {
  display: flex;
  gap: 20px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 16px;
  background: #f8f9fa;
  border-radius: 8px;
  min-width: 80px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  
  &:hover {
    background: #e9ecef;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  &.active {
    background: #e6f7ff;
    border-color: #1890ff;
    box-shadow: 0 2px 8px rgba(24, 144, 255, 0.2);
    
    .stat-label {
      color: #1890ff;
      font-weight: 600;
    }
  }
  
  .stat-label {
    font-size: 12px;
    color: #666;
    margin-bottom: 4px;
    transition: all 0.3s ease;
  }
  
  .stat-value {
    font-size: 18px;
    font-weight: 600;
    color: #333;
    
    &.exposed {
      color: #52c41a;
    }
    
    &.review_requested {
      color: #fa8c16;
    }
    
    &.pending {
      color: #722ed1;
    }
    
    &.hidden {
      color: #f5222d;
    }
  }
}



:deep(.status-chip-wrap) {
  display: flex;
  justify-content: center;
}

:deep(.status-chip) {
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  min-width: 70px;
  border: 1px solid transparent;
  
  &.exposed {
    background-color: #f6ffed !important;
    color: #389e0d !important;
    border-color: #b7eb8f !important;
  }
  
  &.review_requested {
    background-color: #fff7e6 !important;
    color: #d46b08 !important;
    border-color: #ffd591 !important;
  }
  
  &.pending {
    background-color: #f9f0ff !important;
    color: #722ed1 !important;
    border-color: #d3adf7 !important;
  }
  
  &.hidden {
    background-color: #fff1f0 !important;
    color: #cf1322 !important;
    border-color: #ffa39e !important;
  }
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #999;
  
  p {
    margin: 16px 0;
    font-size: 16px;
  }
}

:deep(.yellow-button) {
  background-color: #FFCC32 !important;
  border-color: #FFCC32 !important;
  color: #000 !important;
  
  &:hover {
    background-color: #E6B82E !important;
    border-color: #E6B82E !important;
  }
  
  &:disabled {
    background-color: #d9d9d9 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
  }
}

// 키워드 스타일 (PendingReviewTable과 동일한 스타일)
:deep(.keywords-container) {
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: center;
  max-width: 180px;
  margin: 0 auto;
}

:deep(.keyword-item) {
  display: block;
  padding: 2px 8px;
  background-color: #f0f8ff;
  color: #1890ff;
  border: 1px solid #d6e4ff;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
  line-height: 1.2;
  text-align: center;
  min-width: 60px;
  
  &:hover {
    background-color: #e6f7ff;
    border-color: #91d5ff;
  }
}

// 반응형 스타일
@media screen and (max-width: 768px) {
  .page-title-container {
    flex-direction: column;
    gap: 16px;
  }
  
  .stats-container {
    width: 100%;
    justify-content: space-around;
  }
  
  .search-container {
    flex-direction: column;
    gap: 8px;
    
    .ant-btn {
      width: 100%;
    }
  }
}
</style> 